import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProviderPartition } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import { cloneDeep, differenceBy, xorBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { productDocumentIndexRoute } from '../ProductDocument.Index';
import productDocumentListDealerGroupDocumentsApi from 'models/productManagement/productDocument/listDealerGroups/Api.ProductDocument.ListDealerGroups';
import { ProductDealerGroupDocumentAddForm } from './sections/ProductDocument.DealerGroupDocuments.Add';
import { addLog } from 'assets/components/feedback/Feedback';
import { combineStrings, replaceStringTemplateWithValues } from 'assets/utils/data/String';
import dealerGroupUploadProductDocumentApi from 'models/dealerManagement/dealerGroup/uploadProductDocument/Api.DealerGroup.UploadProductDocument';
import { getLocales, Language } from 'assets/locales/Locale';
import dealerGroupDeleteProductDocumentApi from 'models/dealerManagement/dealerGroup/deleteProductDocument/Api.DealerGroup.DeleteProductDocument';
import {
  documentTranslationDeleteApi,
  documentTranslationListApi,
  documentTranslationSaveApi,
} from 'models/localization/documentTranslation/Model.DocumentTranslation';
import { convertToArray } from 'assets/utils/data/Data';

export enum ProductDealerGroupDocumentsAction {
  ADD = 'add',
  DELETE = 'delete',
}
export type VerifyProductDocumentsAction = Utils.VerifyExtends<
  Module.ProductManagement.ProductDocument.Actions,
  Utils.ValueOf<typeof ProductDealerGroupDocumentsAction>
>;
export default function useProductDealerGroupDocument() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.ProductDocument.Params,
    Module.ProductManagement.ProductDocument.Query
  >({
    route: productDocumentIndexRoute,
  });
  const productDocumentId = pageRouter.params.id ? parseInt(pageRouter.params.id) : null;

  const { lang } = getLocales();
  const [currentView, setView] = useState<ProductDealerGroupDocumentsAction | undefined>();
  const [selectedItems, setSelectedItems] = useState<Model.IProductDealerGroupDocument[]>([]);
  const [selectedTranslatedItems, setSelectedTranslatedItems] = useState<Model.IDocumentTranslation[]>([]);
  const [{ limit, offset, distributorId }, setDealerGroupDocumentFilters] = useState({
    limit: defaultLimit,
    offset: defaultOffset,
    distributorId: undefined,
  });

  const canView = permissions.EDIT_PRODUCT;
  const listDealerGroupDocumentsApi = useApi({
    action: productDocumentListDealerGroupDocumentsApi,
    default: { productDocumentId: productDocumentId },
    body: { productDocumentId } as Api.Core.ProductDocument.ListDealerDocuments.IRequest,
    wait: !canView,
  });

  const documentIds = useMemo(
    () => listDealerGroupDocumentsApi.payload?.data.map((d) => d.id),
    [listDealerGroupDocumentsApi.payload]
  );
  const listDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProductDealerGroupDocument', documentId: documentIds },
      wait: !canView || !documentIds?.length,
    },
    [canView, documentIds]
  );
  function reloadTranslatedDocs() {
    listDocumentTranslationsApi.execute((b) => b);
  }

  const handleUploadDealerGroupDocumentSubmit = async (formData: ProductDealerGroupDocumentAddForm) => {
    const dealerGroups = formData.dealerGroups || [];
    let uploadedFiles = 0;
    let totalFiles = 0;

    for (const dealerGroup of dealerGroups) {
      const docUploadResponse = await dealerGroupUploadProductDocumentApi(
        {
          id: dealerGroup,
          productId: formData.productId,
          productDocumentId: formData.productDocumentId,
        },
        { formFile: formData.formFile, languageIsoCode: Language.english }
      );

      if (docUploadResponse.status === HttpStatus.ok) {
        uploadedFiles += 1;

        if (formData.translatedFile) {
          const translatedDocUploadResponse = await documentTranslationSaveApi({
            documentId: docUploadResponse.payload.documentId,
            documentType: 'ProductDealerGroupDocument',
            languageIsoCode: Language.french,
            formFile: formData.translatedFile,
          });
          if (translatedDocUploadResponse.status === HttpStatus.ok) {
            uploadedFiles += 1;
          }
          totalFiles += 1;
        }
      }
      totalFiles += 1;
    }

    listDealerGroupDocumentsApi.execute((body) => body);

    if (totalFiles !== uploadedFiles) {
      addLog({
        warning: combineStrings(
          ' ',
          `Some files did not get uploaded!`,
          totalFiles > 0 ? `${uploadedFiles}/${totalFiles} files uploaded!` : null
        ),
      });
    } else {
      addLog({
        success: replaceStringTemplateWithValues(lang.wereSuccessfullyUploaded, {
          entity: lang.productDocuments,
        }),
      });
    }

    setView(undefined);
  };

  const handleDeleteDealerGroupDocumentSubmit = async () => {
    const productDealerGroupDocuments = selectedItems || [];
    const translatedDocs = cloneDeep(selectedTranslatedItems) || [];
    let deletedFiles = 0;
    let totalFiles = 0;

    for (const productDealerGroupDocument of productDealerGroupDocuments) {
      const docDeleteReponse = await dealerGroupDeleteProductDocumentApi({
        productDealerGroupDocumentId: productDealerGroupDocument.id,
      });
      if (docDeleteReponse.status === HttpStatus.ok) {
        deletedFiles += 1;
      }
      totalFiles += 1;
      const tempDocs = listDocumentTranslationsApi.payload?.data.filter(
        (d) => d.documentId === productDealerGroupDocument.id
      );
      const diffDocs = differenceBy(tempDocs, translatedDocs, (it) => it.id);
      translatedDocs.push(...diffDocs);
    }

    for (const translatedDoc of translatedDocs) {
      const translationDocDeleteReponse = await documentTranslationDeleteApi({
        id: translatedDoc.id,
      });
      if (translationDocDeleteReponse.status === HttpStatus.ok) {
        deletedFiles += 1;
      }
      totalFiles += 1;
    }

    listDealerGroupDocumentsApi.execute((body) => body);

    if (totalFiles !== deletedFiles) {
      addLog({
        warning: combineStrings(
          ' ',
          `Some files did not get deleted!`,
          totalFiles > 0 ? `${deletedFiles}/${totalFiles} files deleted!` : null
        ),
      });
    } else {
      addLog({
        success: replaceStringTemplateWithValues(lang.wereSuccessfullyDeleted, {
          entity: lang.productDocuments,
        }),
      });
    }

    setView(undefined);
    setSelectedItems([]);
    setSelectedTranslatedItems([]);
  };

  const toggleItem = (productDealerGroupDocument: Model.IProductDealerGroupDocument) => {
    const newSelectedItemList = xorBy(selectedItems || [], [productDealerGroupDocument], 'dealerGroupId');
    setSelectedItems(newSelectedItemList);
  };

  const toggleTranslatedItem = (translatedDoc: Utils.SingleOrArray<Model.IDocumentTranslation>) => {
    const newSelectedItemList = xorBy(selectedTranslatedItems || [], convertToArray(translatedDoc), 'documentId');
    setSelectedTranslatedItems(newSelectedItemList);
  };

  const toggleSelectAll = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedItems(listDealerGroupDocumentsApi.payload?.data ?? []);
    } else {
      setSelectedItems([]);
    }
  };

  const toggleSelectAllTranslations = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedTranslatedItems(listDocumentTranslationsApi.payload?.data ?? []);
    } else {
      setSelectedTranslatedItems([]);
    }
  };

  const [filteredDealerGroupDocuments, filteredDealerGroupDocumentsCount] = useMemo(() => {
    const filteredList = listDealerGroupDocumentsApi.payload?.data?.filter(
      (dealerGroupDocument) => !distributorId || dealerGroupDocument.dealerGroup.distributorId === distributorId
    );

    return [filteredList?.slice(offset * limit, offset * limit + limit), filteredList?.length];
  }, [listDealerGroupDocumentsApi.payload, limit, offset, distributorId]);

  useEffect(() => {
    setSelectedItems([]);
  }, [listDealerGroupDocumentsApi.payload?.data]);

  return {
    currentView,
    setView,
    selectedItems,
    toggleItem,
    toggleSelectAll,
    selectedTranslatedItems,
    toggleTranslatedItem,
    toggleSelectAllTranslations,
    handleDeleteDealerGroupDocumentSubmit,
    handleUploadDealerGroupDocumentSubmit,
    permissions,
    fullDealerGroupDocumentList: listDealerGroupDocumentsApi?.payload?.data,
    translatedDocumentList: listDocumentTranslationsApi?.payload?.data ?? [],
    reloadTranslatedDocs,
    dealerGroupDocumentList: {
      limit,
      offset,
      distributorId,
      setDealerGroupDocumentFilters,
      data: filteredDealerGroupDocuments,
      count: filteredDealerGroupDocumentsCount,
      applyFilters: ({ distributorId }: { distributorId?: number }) => {
        setDealerGroupDocumentFilters((old) => ({ ...old, distributorId, offset: 0 }));
      },
      resetFilters: () => {
        setDealerGroupDocumentFilters({ limit: defaultLimit, offset: defaultOffset, distributorId: undefined });
      },
    },
    views: {
      [ProductDealerGroupDocumentsAction.DELETE]: currentView === ProductDealerGroupDocumentsAction.DELETE,
      [ProductDealerGroupDocumentsAction.ADD]: currentView === ProductDealerGroupDocumentsAction.ADD,
    },
  };
}

export function useProductDealerGroupDocumentProvider() {
  return useDataProviderPartition<ReturnType<typeof useProductDealerGroupDocument>>('productDealerGroupDocumentHook');
}
